import {
  MotifButton,
  MotifCard,
  MotifCardBody,
  MotifCardHeader,
  MotifChip,
  MotifIcon,
  MotifProgressLoader,
  MotifTooltip,
} from '@ey-xd/motif-react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import './SODResult.scss'
import Table from '@/components/Table/table'

import { EvidenceType, JobRecordStatusType } from '@/services/types'
import type { JSX } from 'react'
import {
  useGetSodSummaryQuery,
  useLazyGetSodFileQuery,
  useGetSodTotalsQuery,
  useCreateSodAnalysisMutation,
} from '@/services/sodanalysis/sodReducer'
import { MotifToast } from '@ey-xd/motif-react'
import { useParams } from 'react-router-dom'
import { IconoirArrowDown, IconoirInfoCircle } from '@ey-xd/motif-icon'
import { useDispatch, useSelector } from 'react-redux'
import { setDisableGlobalErrorPopup } from '@/features/errors/errors-slice'

interface SODResultProps {
  setCanMarkAnalysisComplete: (value: boolean) => void
}

// JobRecordStatusType is not numerically ordered by "how far along" a job is
// (e.g. Submitted = 8 is a non-terminal, in-flight status that sorts above
// the terminal Completed = 6 / Failed = 7), so terminal-ness must be checked
// explicitly rather than via >= / < comparisons against Completed.
const isSodJobTerminal = (status: number) =>
  status === JobRecordStatusType.Completed || status === JobRecordStatusType.Failed

const SODResult: React.FC<SODResultProps> = ({
  setCanMarkAnalysisComplete,
}) => {
  const [triggerGetSodFile, { isLoading: isDownloading }] =
    useLazyGetSodFileQuery()
  const [createSodAnalysis, { isLoading: isRegenerating }] =
    useCreateSodAnalysisMutation()
  const [sodTotals, setSodTotals] = useState({
    totalUsers: 0,
    totalDevelopers: 0,
    totalImplementers: 0,
    sodConflicts: 0,
    sodJobStatus: 0,
    sodJobStatusDescription: '',
  })
  const { applicationId } = useParams<{
    applicationId: string
  }>()

  const sodId = useSelector((state: { sod: { sodId: string | null } }) => {
    return state.sod?.sodId
  })
  const { data: developersData, refetch: refetchDevelopersData } =
    useGetSodSummaryQuery({
      applicationId,
      evidenceType: EvidenceType.Developer,
    })

  const { data: implementersData, refetch: refetchImplementersData } =
    useGetSodSummaryQuery({
      applicationId,
      evidenceType: EvidenceType.Implementer,
    })

  const {
    data: sodTotalsData,
    isLoading: isSodTotalsLoading,
    isError: isSodTotalsError,
    refetch: refetchSodTotals,
  } = useGetSodTotalsQuery(applicationId)

  const dispatch = useDispatch()

  useEffect(() => {
    if (!sodTotalsData) return

    setSodTotals({
      totalUsers: sodTotalsData.totalUsers || 0,
      totalImplementers: sodTotalsData.totalImplementers || 0,
      totalDevelopers: sodTotalsData.totalDevelopers || 0,
      sodConflicts: sodTotalsData.totalSODConflicts || 0,
      sodJobStatus: sodTotalsData.sodJobStatus || 0,
      sodJobStatusDescription: sodTotalsData.sodJobStatusDescription || '',
    })

    if (isSodJobTerminal(sodTotalsData.sodJobStatus)) {
      // Job finished — refresh the summary tables once after a short delay
      const refreshTimer = setTimeout(() => {
        refetchDevelopersData()
        refetchImplementersData()
      }, 3000)
      return () => clearTimeout(refreshTimer)
    }

    // Job still in progress — chain polls from the refetch promise so:
    // (a) requests never overlap (next poll waits for the previous response), and
    // (b) polling continues even when RTK Query's structural sharing keeps the
    //     sodTotalsData reference unchanged (which would prevent a useEffect re-run).
    let active = true
    let pollTimer: ReturnType<typeof setTimeout> | null = null
    const poll = async () => {
      if (!active) return
      try { await refetchSodTotals() } catch { /* reschedule on network error too */ }
      if (active) pollTimer = setTimeout(poll, 3000)
    }
    pollTimer = setTimeout(poll, 3000)
    return () => {
      active = false
      if (pollTimer) clearTimeout(pollTimer)
    }
  }, [sodTotalsData])

  const isJobPending =
    isSodTotalsLoading || !isSodJobTerminal(sodTotals.sodJobStatus)

  const BadgeCount = ({
    value,
    className,
  }: {
    value: number
    className?: string
  }): JSX.Element =>
    isJobPending ? (
      <div className="sod-badge-loader">
        <div style={{ position: 'relative', width: 28, height: 28 }}>
          <MotifProgressLoader show />
        </div>
      </div>
    ) : (
      <span className={className}>{value}</span>
    )

  const [filterType, setFilterType] = useState('All')

  const columnDefs = useMemo(
    () => [
      { headerName: 'Developer ID', field: 'developerId', minWidth: 150 },
      { headerName: 'Developer Name', field: 'developerName', minWidth: 150 },
      { headerName: 'Developer Email', field: 'developerEmail', minWidth: 200 },
      { headerName: 'User Group', field: 'userGroup', minWidth: 150 },
      { headerName: 'User Permission', field: 'userPermission', minWidth: 150 },
      { headerName: 'Department', field: 'department', minWidth: 150 },
      { headerName: 'Job Title', field: 'jobTitle', minWidth: 150 },
      { headerName: 'Environment', field: 'environment', minWidth: 150 },
      { headerName: 'SOD Conclusion', field: 'results', minWidth: 150 },
    ],
    [],
  )

  const columnDefsImp = useMemo(
    () => [
      { headerName: 'Implementor ID', field: 'developerId', minWidth: 150 },
      { headerName: 'Implementor Name', field: 'developerName', minWidth: 150 },
      { headerName: 'Implementor Email', field: 'developerEmail', minWidth: 200 },
      { headerName: 'User Group', field: 'userGroup', minWidth: 150 },
      { headerName: 'User Permission', field: 'userPermission', minWidth: 150 },
      { headerName: 'Department', field: 'department', minWidth: 150 },
      { headerName: 'Job Title', field: 'jobTitle', minWidth: 150 },
      { headerName: 'Environment', field: 'environment', minWidth: 150 },
      { headerName: 'SOD Conclusion', field: 'results', minWidth: 150 },
    ],
    [],
  )

  const handleDownloadSODExcel = async () => {
    if (!sodId) {
      return
    }
    try {
      await createSodAnalysis(sodId).unwrap()
      const fileBlob = await triggerGetSodFile(sodId).unwrap()
      const url = window.URL.createObjectURL(fileBlob)
      const link = document.createElement('a')
      link.href = url
      link.download = `SOD_File_${sodId}.xlsx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (error) {
      console.error('Error downloading file:', error)
    }
  }

  useEffect(() => {
    dispatch(setDisableGlobalErrorPopup(true))
    return () => {
      dispatch(setDisableGlobalErrorPopup(true))
    }
  }, [dispatch])

  const [toast, setToast] = useState<{ show: boolean; message: string }>({
    show: false,
    message: '',
  })

  const handleToastClose = () => {
    setToast({
      show: false,
      message: '',
    })
  }

  useEffect(() => {
    if (toast.show) {
      setTimeout(() => {
        setToast({
          show: false,
          message: '',
        })
      }, 6000)
    }
  }, [toast.show])

  return (
    <div>
      <main className="main-content">
        {toast.show && (
          <div className="sod-toast-row">
            <MotifToast onClose={() => handleToastClose()} variant="error">
              {toast.message}
            </MotifToast>
          </div>
        )}
        <div
          className="statistics-cards"
          style={{ display: 'flex', gap: '16px' }}
        >
          <MotifCard className="card" style={{ flex: 1 }}>
            <MotifCardHeader>Total Users</MotifCardHeader>
            <MotifCardBody>
              <BadgeCount value={sodTotals.totalUsers} />
            </MotifCardBody>
          </MotifCard>
          <MotifCard className="card" style={{ flex: 1 }}>
            <MotifCardHeader>Total Developers</MotifCardHeader>
            <MotifCardBody>
              <BadgeCount value={sodTotals.totalDevelopers} />
            </MotifCardBody>
          </MotifCard>
          <MotifCard className="card" style={{ flex: 1 }}>
            <MotifCardHeader>Total Implementers</MotifCardHeader>
            <MotifCardBody>
              <BadgeCount value={sodTotals.totalImplementers} />
            </MotifCardBody>
          </MotifCard>
          <MotifCard className="card conflict" style={{ flex: 1 }}>
            <MotifCardHeader className="conflict-number">
              SOD Conflict
            </MotifCardHeader>
            <MotifCardBody>
              <BadgeCount
                value={sodTotals.sodConflicts}
                className="conflict-number"
              />
            </MotifCardBody>
          </MotifCard>
        </div>
        <div className="motif-row">
          <div className="motif-col-lg-12">
            <MotifCard
              className="results-list-container"
              orientation="horizontal"
              variant="full-width"
            >
              <MotifCardHeader>
                <div className="table-header-actions">
                  <div> &nbsp;</div>
                  <MotifButton
                    style={{ marginRight: '10px' }}
                    onClick={handleDownloadSODExcel}
                    disabled={!sodId || isDownloading || isRegenerating}
                    type="button"
                    size="small"
                    variant="primary-alt"
                  >
                    <MotifTooltip
                      allowClick
                      allowHover
                      hideCloseButton
                      flip
                      forceHideCounter={0}
                      id="tooltip"
                      offset={[0, 8]}
                      placement="top"
                      style={{ marginLeft: '10px' }}
                      trigger={
                        <MotifIcon
                          src={IconoirInfoCircle}
                          fill="black"
                          stroke="black"
                        />
                      }
                      usePortal
                      variant="default"
                    >
                      <div className="motif-caption-default-light motif-tooltip-custom-width">
                        Please make sure, you mark SOD analysis complete before
                        downloading the excel file.
                      </div>
                    </MotifTooltip>
                    <MotifIcon iconFunction={IconoirArrowDown} />
                    {isRegenerating
                      ? 'Generating...'
                      : isDownloading
                        ? 'Downloading...'
                        : 'Download excel'}
                  </MotifButton>
                </div>
              </MotifCardHeader>
              <MotifCardBody>
                <div
                  style={{
                    display: 'flex',
                    gap: '32px',
                    width: '100%',
                    alignItems: 'stretch',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Table
                      columnDefs={columnDefs}
                      cellRenderers={developersData || []}
                      title="Developers"
                      description=""
                      TitleComplement={() => <div />}
                      pagination
                    />
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Table
                      columnDefs={columnDefsImp}
                      cellRenderers={implementersData || []}
                      title="Implementers"
                      description=""
                      TitleComplement={() => <div />}
                      pagination
                    />
                  </div>
                </div>
              </MotifCardBody>
            </MotifCard>
          </div>
        </div>
      </main>
    </div>
  )
}

export default SODResult
